import { useState, useEffect } from 'react'
import { db } from '../firebase/config'
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore'
import { Search, Calendar, MapPin, Clock, Users, ChevronLeft, ChevronRight, X, UserCheck, Heart } from 'lucide-react'
import { toast } from '../hooks/useToast'

function EventsPage({ currentUser }) {
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [pastEvents, setPastEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [activeTab, setActiveTab] = useState('upcoming')
  const [lightboxImage, setLightboxImage] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [userInterests, setUserInterests] = useState({}) // { eventId: 'interested' | 'participating' }

  const itemsPerPage = 9

  useEffect(() => {
    loadEvents()
    loadUserInterests()
  }, [])

  const loadUserInterests = () => {
    try {
      const saved = localStorage.getItem('eventInterests')
      if (saved) {
        setUserInterests(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Erreur chargement intérêts:', error)
    }
  }

  const saveUserInterests = (interests) => {
    try {
      localStorage.setItem('eventInterests', JSON.stringify(interests))
    } catch (error) {
      console.error('Erreur sauvegarde intérêts:', error)
    }
  }

  const handleInterest = async (eventId, type) => {
    if (!currentUser) {
      toast.error('🔒 Connectez-vous pour manifester votre intérêt')
      return
    }

    const currentInterest = userInterests[eventId]
    let newInterests = { ...userInterests }

    // Si on clique sur le même type, on le retire
    if (currentInterest === type) {
      delete newInterests[eventId]
      toast.success('✅ Intérêt retiré')
    } else {
      // Sinon on change le type
      newInterests[eventId] = type
      if (type === 'interested') {
        toast.success('💙 Vous êtes intéressé par cet événement !')
      } else {
        toast.success('✅ Vous participez à cet événement !')
      }
    }

    setUserInterests(newInterests)
    saveUserInterests(newInterests)

    // Optionnel : mettre à jour Firebase
    try {
      const eventRef = doc(db, '100johnny', 'main', 'radio_events', eventId)
      if (currentInterest === type) {
        await updateDoc(eventRef, {
          [`${type}Users`]: arrayRemove(currentUser.uid)
        })
      } else {
        await updateDoc(eventRef, {
          [`${type}Users`]: arrayUnion(currentUser.uid)
        })
        // Retirer de l'autre liste si on change de type
        if (currentInterest) {
          await updateDoc(eventRef, {
            [`${currentInterest}Users`]: arrayRemove(currentUser.uid)
          })
        }
      }
    } catch (error) {
      console.error('Erreur mise à jour Firebase:', error)
    }
  }

  const loadEvents = async () => {
    setLoading(true)
    
    try {
      const upcomingSnapshot = await getDocs(collection(db, '100johnny', 'main', 'radio_events'))
      const upcomingData = upcomingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'upcoming'
      }))
      
      upcomingData.sort((a, b) => {
        if (a.date && b.date) {
          const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date)
          const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date)
          return dateA - dateB
        }
        return 0
      })
      
      setUpcomingEvents(upcomingData)
      
      const pastSnapshot = await getDocs(collection(db, '100johnny', 'main', 'old_events'))
      const pastData = pastSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'past'
      }))
      
      pastData.sort((a, b) => {
        if (a.date && b.date) {
          const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date)
          const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date)
          return dateB - dateA
        }
        return 0
      })
      
      setPastEvents(pastData)
      
    } catch (error) {
      console.error('Erreur chargement événements:', error)
      setUpcomingEvents([])
      setPastEvents([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Date non définie'
    try {
      const d = date.toDate ? date.toDate() : new Date(date)
      return d.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    } catch (e) {
      return 'Date invalide'
    }
  }

  const formatTime = (time) => {
    if (!time) return ''
    return time
  }

  const getStatusBadge = (status) => {
    const badges = {
      'upcoming': { text: '🔜 À venir', color: '#00d9ff', bg: 'rgba(0, 217, 255, 0.2)', border: 'rgba(0, 217, 255, 0.4)' },
      'completed': { text: '✅ Terminé', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.4)' },
      'cancelled': { text: '❌ Annulé', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.4)' }
    }
    
    return badges[status] || badges.upcoming
  }

  const getEventImages = (event) => {
    const images = []
    
    if (event.photoURL) {
      if (typeof event.photoURL === 'string') {
        images.push(event.photoURL)
      }
    }
    
    if (event.images && Array.isArray(event.images)) {
      event.images.forEach(img => {
        if (typeof img === 'string') {
          images.push(img)
        } else if (img.url) {
          images.push(img.url)
        }
      })
    }
    
    return [...new Set(images)].filter(url => url && url.startsWith('http'))
  }

  const currentEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents

  const filteredEvents = currentEvents.filter(event => {
    const search = searchTerm.toLowerCase()
    return (
      event.name?.toLowerCase().includes(search) ||
      event.location?.toLowerCase().includes(search) ||
      event.city?.toLowerCase().includes(search) ||
      event.description?.toLowerCase().includes(search)
    )
  })

  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentPageEvents = filteredEvents.slice(startIndex, startIndex + itemsPerPage)

  if (selectedEvent) {
    const images = getEventImages(selectedEvent)
    const statusBadge = getStatusBadge(selectedEvent.status)
    const userInterest = userInterests[selectedEvent.id]
    const isUpcoming = selectedEvent.type === 'upcoming'
    
    return (
      <div className="page active">
        <div className="container">
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setSelectedEvent(null)
              setCurrentImageIndex(0)
            }}
            style={{ marginBottom: '20px' }}
          >
            ← Retour aux événements
          </button>

          <div className="glass-card">
            {images.length > 0 && (
              <div style={{ marginBottom: '20px', position: 'relative' }}>
                <img 
                  src={images[currentImageIndex]} 
                  alt={selectedEvent.name}
                  onClick={() => setLightboxImage(images[currentImageIndex])}
                  style={{ 
                    width: '100%', 
                    maxHeight: '500px', 
                    objectFit: 'cover',
                    borderRadius: '15px',
                    cursor: 'pointer',
                    transition: 'transform 0.3s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.01)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                />
                
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)}
                      style={{
                        position: 'absolute',
                        left: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <ChevronLeft size={24} color="#fff" />
                    </button>
                    
                    <button
                      onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)}
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0, 0, 0, 0.7)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <ChevronRight size={24} color="#fff" />
                    </button>
                    
                    <div style={{
                      position: 'absolute',
                      bottom: '15px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: '8px'
                    }}>
                      {images.map((_, idx) => (
                        <div
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: idx === currentImageIndex ? '#00d9ff' : 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </div>
                    
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      padding: '8px 15px',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}>
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={{ marginBottom: '15px' }}>
              <span style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: statusBadge.bg,
                border: `1px solid ${statusBadge.border}`,
                borderRadius: '12px',
                fontSize: '0.9rem',
                color: statusBadge.color,
                fontWeight: 700
              }}>
                {statusBadge.text}
              </span>
            </div>

            <h1 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 900, 
              marginBottom: '20px',
              lineHeight: 1.2,
              background: 'linear-gradient(135deg, #00d9ff, #ffd700)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {selectedEvent.name || 'Événement sans nom'}
            </h1>

            {/* Boutons Intéressé / J'y participe (seulement pour événements à venir) */}
            {isUpcoming && (
              <div style={{ 
                display: 'flex', 
                gap: '15px', 
                marginBottom: '30px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handleInterest(selectedEvent.id, 'interested')}
                  className="btn"
                  style={{
                    padding: '15px 25px',
                    background: userInterest === 'interested' 
                      ? 'linear-gradient(135deg, #0ea5e9, #0284c7)' 
                      : 'rgba(14, 165, 233, 0.2)',
                    border: userInterest === 'interested' ? 'none' : '1px solid rgba(14, 165, 233, 0.4)',
                    color: userInterest === 'interested' ? '#fff' : '#0ea5e9',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '1rem',
                    transform: userInterest === 'interested' ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Heart size={20} fill={userInterest === 'interested' ? '#fff' : 'none'} />
                  {userInterest === 'interested' ? 'Intéressé ✓' : 'Je suis intéressé'}
                </button>

                <button
                  onClick={() => handleInterest(selectedEvent.id, 'participating')}
                  className="btn"
                  style={{
                    padding: '15px 25px',
                    background: userInterest === 'participating' 
                      ? 'linear-gradient(135deg, #22c55e, #16a34a)' 
                      : 'rgba(34, 197, 94, 0.2)',
                    border: userInterest === 'participating' ? 'none' : '1px solid rgba(34, 197, 94, 0.4)',
                    color: userInterest === 'participating' ? '#fff' : '#22c55e',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '1rem',
                    transform: userInterest === 'participating' ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <UserCheck size={20} />
                  {userInterest === 'participating' ? 'Je participe ✓' : 'J\'y participe'}
                </button>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '15px',
              marginBottom: '30px'
            }}>
              {selectedEvent.date && (
                <div style={{
                  padding: '20px',
                  background: 'rgba(0, 217, 255, 0.1)',
                  borderRadius: '15px',
                  border: '1px solid rgba(0, 217, 255, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <Calendar size={20} color="#00d9ff" />
                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>DATE</span>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#00d9ff' }}>
                    {formatDate(selectedEvent.date)}
                  </div>
                </div>
              )}

              {selectedEvent.time && (
                <div style={{
                  padding: '20px',
                  background: 'rgba(255, 215, 0, 0.1)',
                  borderRadius: '15px',
                  border: '1px solid rgba(255, 215, 0, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <Clock size={20} color="#ffd700" />
                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>HEURE</span>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ffd700' }}>
                    {formatTime(selectedEvent.time)}
                  </div>
                </div>
              )}

              {selectedEvent.location && (
                <div style={{
                  padding: '20px',
                  background: 'rgba(255, 20, 147, 0.1)',
                  borderRadius: '15px',
                  border: '1px solid rgba(255, 20, 147, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <MapPin size={20} color="#ff1493" />
                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>LIEU</span>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ff1493' }}>
                    {selectedEvent.location}
                    {selectedEvent.city && `, ${selectedEvent.city}`}
                  </div>
                </div>
              )}

              {selectedEvent.capacity && (
                <div style={{
                  padding: '20px',
                  background: 'rgba(147, 51, 234, 0.1)',
                  borderRadius: '15px',
                  border: '1px solid rgba(147, 51, 234, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <Users size={20} color="#9333ea" />
                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>CAPACITÉ</span>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#9333ea' }}>
                    {selectedEvent.capacity} places
                  </div>
                </div>
              )}
            </div>

            {selectedEvent.description && (
              <div style={{
                padding: '25px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '15px',
                border: '1px solid rgba(255,255,255,0.1)',
                marginTop: '30px'
              }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 700, 
                  marginBottom: '15px',
                  color: '#00d9ff'
                }}>
                  📝 Description
                </h3>
                <p style={{ 
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  color: 'rgba(255,255,255,0.9)',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedEvent.description}
                </p>
              </div>
            )}
          </div>

          <div className="dock-spacer"></div>
        </div>

        {lightboxImage && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px',
              cursor: 'pointer'
            }}
            onClick={() => setLightboxImage(null)}
          >
            <button
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation()
                setLightboxImage(null)
              }}
            >
              <X size={24} color="#fff" />
            </button>
            <img 
              src={lightboxImage} 
              alt="Image en grand"
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '10px'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="page active">
      <div className="container">
        <div className="page-header">
          <div className="page-badge">📅 ÉVÉNEMENTS</div>
          <h1 className="page-title">Événements Johnny</h1>
          <p className="page-subtitle">Concerts, rencontres et célébrations</p>
        </div>

        {/* Onglets PLEINE LARGEUR avec TEXTE BLANC */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '20px',
          width: '100%'
        }}>
          <button
            onClick={() => {
              setActiveTab('upcoming')
              setCurrentPage(1)
            }}
            className="btn"
            style={{
              flex: 1,
              padding: '15px 30px',
              background: activeTab === 'upcoming' ? 'linear-gradient(135deg, #00d9ff, #0ea5e9)' : 'rgba(255,255,255,0.05)',
              border: activeTab === 'upcoming' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              borderRadius: '15px',
              fontWeight: 700,
              fontSize: '1rem',
              color: '#fff'
            }}
          >
            🔜 À venir ({upcomingEvents.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('past')
              setCurrentPage(1)
            }}
            className="btn"
            style={{
              flex: 1,
              padding: '15px 30px',
              background: activeTab === 'past' ? 'linear-gradient(135deg, #ffd700, #f59e0b)' : 'rgba(255,255,255,0.05)',
              border: activeTab === 'past' ? 'none' : '1px solid rgba(255,255,255,0.2)',
              borderRadius: '15px',
              fontWeight: 700,
              fontSize: '1rem',
              color: '#fff'
            }}
          >
            📚 Passés ({pastEvents.length})
          </button>
        </div>

        <div className="glass-card" style={{ marginBottom: '20px', padding: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Search size={20} color="rgba(255,255,255,0.5)" />
            <input
              type="text"
              placeholder="Rechercher un événement..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: '#fff',
                outline: 'none',
                fontSize: '1rem'
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Chargement des événements...</p>
          </div>
        ) : currentPageEvents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <p>{searchTerm ? 'Aucun événement trouvé' : `Aucun événement ${activeTab === 'upcoming' ? 'à venir' : 'passé'} pour le moment`}</p>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {currentPageEvents.map((event) => {
                const images = getEventImages(event)
                const coverImage = images[0]
                const statusBadge = getStatusBadge(event.status)
                const userInterest = userInterests[event.id]
                const isUpcoming = event.type === 'upcoming'
                
                return (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    style={{
                      cursor: 'pointer',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)'
                      e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 217, 255, 0.3)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    {coverImage && (
                      <div style={{ position: 'relative' }}>
                        <img 
                          src={coverImage}
                          alt={event.name}
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: statusBadge.bg,
                          border: `1px solid ${statusBadge.border}`,
                          padding: '6px 12px',
                          borderRadius: '10px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: statusBadge.color
                        }}>
                          {statusBadge.text}
                        </div>
                        {images.length > 1 && (
                          <div style={{
                            position: 'absolute',
                            bottom: '10px',
                            right: '10px',
                            background: 'rgba(0, 0, 0, 0.8)',
                            padding: '6px 12px',
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            📷 {images.length}
                          </div>
                        )}
                        {/* Badge intéressé/participe sur la carte */}
                        {isUpcoming && userInterest && (
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            background: userInterest === 'interested' 
                              ? 'linear-gradient(135deg, #0ea5e9, #0284c7)' 
                              : 'linear-gradient(135deg, #22c55e, #16a34a)',
                            padding: '6px 12px',
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            {userInterest === 'interested' ? '💙' : '✅'} 
                            {userInterest === 'interested' ? 'Intéressé' : 'Je participe'}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div style={{ padding: '20px' }}>
                      <h3 style={{
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        marginBottom: '15px',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {event.name || 'Événement sans nom'}
                      </h3>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {event.date && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                            <Calendar size={16} color="#00d9ff" />
                            {formatDate(event.date)}
                          </div>
                        )}
                        
                        {event.time && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                            <Clock size={16} color="#ffd700" />
                            {formatTime(event.time)}
                          </div>
                        )}
                        
                        {event.location && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                            <MapPin size={16} color="#ff1493" />
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {event.location}{event.city && `, ${event.city}`}
                            </span>
                          </div>
                        )}
                        
                        {event.capacity && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                            <Users size={16} color="#9333ea" />
                            {event.capacity} places
                          </div>
                        )}
                      </div>

                      {event.description && (
                        <p style={{
                          marginTop: '15px',
                          fontSize: '0.9rem',
                          color: 'rgba(255,255,255,0.6)',
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {event.description}
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px',
                marginTop: '30px',
                flexWrap: 'wrap'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                >
                  ← Précédent
                </button>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '0 15px'
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Page {currentPage} sur {totalPages}
                  </span>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                >
                  Suivant →
                </button>
              </div>
            )}

            <div style={{ 
              textAlign: 'center', 
              marginTop: '15px',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.9rem'
            }}>
              {filteredEvents.length} événement{filteredEvents.length > 1 ? 's' : ''} {activeTab === 'upcoming' ? 'à venir' : 'passé(s)'}
            </div>
          </>
        )}

      </div>
    </div>
  )
}

export default EventsPage